"use client";

import { PopText, Reveal } from "@/components/motion/reveal";
import { useLivePrices } from "@/components/providers/prices-provider";
import { AssetIcon } from "@/components/ui/asset-icon";
import { GradientText } from "@/components/ui/gradient-text";
import { Marquee } from "@/components/ui/marquee";
import { formatUsd } from "@/lib/format";
import { CRYPTO, STOCKS, type Asset } from "@/lib/landing-data";
import { cn } from "@/lib/utils";

function QuoteChip({ asset }: { asset: Asset }) {
  const live = useLivePrices();
  const liveData = live[asset.symbol];
  const change = liveData?.change24h ?? asset.change24h ?? 0;
  const up = change >= 0;
  const priceLabel = liveData ? formatUsd(liveData.price) : asset.price;
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border/60 bg-card/60 px-4 py-2.5 backdrop-blur-sm">
      <AssetIcon asset={asset} size={32} />
      <div className="leading-tight">
        <p className="text-sm font-semibold">{asset.symbol}</p>
        <p className="text-xs text-muted-foreground">{asset.name}</p>
      </div>
      <div className="ml-3 text-right leading-tight">
        <p className="text-sm font-semibold tabular-nums">{priceLabel}</p>
        <p className={cn("text-xs font-medium tabular-nums", up ? "text-emerald-400" : "text-rose-400")}>
          {up ? "+" : ""}
          {change.toFixed(1)}%
        </p>
      </div>
    </div>
  );
}

const STOCK_ROW = Object.values(STOCKS);
const CRYPTO_ROW = Object.values(CRYPTO);

export function StocksCrypto() {
  return (
    <section id="stocks" className="relative overflow-hidden py-16 sm:py-20">
      <Reveal className="mx-auto mb-12 max-w-2xl px-4 text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Stocks + crypto</p>
        <h2 className="mt-3 text-balance text-4xl font-semibold tracking-tight sm:text-5xl">
          <PopText className="block">Wall Street meets</PopText>
          <PopText className="block" delay={0.18}>
            <GradientText>the chain.</GradientText>
          </PopText>
        </h2>
        <p className="mx-auto mt-4 hidden max-w-xl text-lg text-muted-foreground md:block">
          Trade tokenized stocks and crypto side by side — 24/7, no brokerage account, settled onchain.
        </p>
      </Reveal>

      <div className="relative flex flex-col gap-4">
        {/* edge fades */}
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-background to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-background to-transparent" />

        <Marquee pauseOnHover className="[--duration:48s]">
          {STOCK_ROW.map((a) => (
            <QuoteChip key={a.symbol} asset={a} />
          ))}
        </Marquee>
        <Marquee reverse pauseOnHover className="[--duration:56s]">
          {CRYPTO_ROW.map((a) => (
            <QuoteChip key={a.symbol} asset={a} />
          ))}
        </Marquee>
      </div>
    </section>
  );
}
